import type { Node } from '@antv/x6';
import { isInvocationGuideEdge } from './mergeSnap';

export const DRAG_THRESHOLD = 5;
export const PARENT_PAD = 10;
export const OBJECT_HEADER_HEIGHT = 22;

export const EDGE_VERTEX_TOOL = {
  name: 'vertices',
  args: {
    attrs: { fill: '#1890ff', stroke: '#fff', strokeWidth: 1, r: 5 },
    snapRadius: 10,
    addable: true,
    removable: true,
  },
};

export const typeStrokeColors: Record<string, string> = {
  object: '#70E483',
  option: '#70E483',
  process: '#3BC3FF',
  state: '#fa8c16',
  value: '#4169E1',
};

export const selectedStrokeColor = '#1890ff';

export type EdgeAnchorName = 'top' | 'bottom' | 'left' | 'right';

export type ClipboardNode = {
  id: string;
  shape: string;
  data: Record<string, any>;
  position: { x: number; y: number };
  size: { width: number; height: number };
  children: Array<{
    id: string;
    shape: string;
    data: Record<string, any>;
    position: { x: number; y: number };
    size: { width: number; height: number };
  }>;
};

export type ClipboardEdge = {
  id: string;
  shape: string;
  data: Record<string, any>;
  sourceId: string;
  targetId: string;
  sourcePort?: string;
  targetPort?: string;
  vertices?: { x: number; y: number }[];
  router?: any;
  connector?: any;
  attrs?: Record<string, any>;
};

export type ClipboardPayload = {
  nodes: ClipboardNode[];
  edges: ClipboardEdge[];
};

export function clampNodeToParent(
  node: any,
  parent: any,
  targetRelX: number,
  targetRelY: number,
): { x: number; y: number } {
  const parentType = parent.getData()?.type;
  const parentSize = parent.getSize();
  const nodeSize = node.getSize();

  if (parentType === 'process') {
    const centerX = parentSize.width / 2;
    const centerY = parentSize.height / 2;
    const rx = Math.max(0, centerX - PARENT_PAD - nodeSize.width / 2);
    const ry = Math.max(0, centerY - PARENT_PAD - nodeSize.height / 2);

    let relCenterX = targetRelX + nodeSize.width / 2;
    let relCenterY = targetRelY + nodeSize.height / 2;

    const dx = relCenterX - centerX;
    const dy = relCenterY - centerY;
    const distance = (dx * dx) / (rx * rx) + (dy * dy) / (ry * ry);

    if (distance > 1) {
      const scale = 1 / Math.sqrt(distance);
      relCenterX = centerX + dx * scale;
      relCenterY = centerY + dy * scale;
    }

    return { x: relCenterX - nodeSize.width / 2, y: relCenterY - nodeSize.height / 2 };
  }

  const minX = PARENT_PAD;
  const isObjectLike = parentType === 'object' || parentType === 'option';
  const minY = isObjectLike ? OBJECT_HEADER_HEIGHT + PARENT_PAD : PARENT_PAD;
  const maxX = Math.max(minX, parentSize.width - nodeSize.width - PARENT_PAD);
  const maxY = Math.max(minY, parentSize.height - nodeSize.height - PARENT_PAD);

  return {
    x: Math.min(Math.max(targetRelX, minX), maxX),
    y: Math.min(Math.max(targetRelY, minY), maxY),
  };
}

export function applyNodeSelection(node: any, selected: boolean): void {
  const type = node.getData()?.type || 'object';
  const originalColor = typeStrokeColors[type] || typeStrokeColors.object;
  const mode = node.getData()?.selectionMode;
  const shouldHighlight = selected && mode !== 'group';

  node.attr('body/stroke', shouldHighlight ? selectedStrokeColor : originalColor);
  node.setData({ selected });
}

export function showNodeControls(node: any): void {
  node.attr({
    body: { strokeWidth: 3, stroke: '#1890ff' },
    portTop: { circle: { opacity: 1 } },
    portBottom: { circle: { opacity: 1 } },
    portLeft: { circle: { opacity: 1 } },
    portRight: { circle: { opacity: 1 } },
  });
}

export function hideNodeControls(node: any): void {
  node.attr({
    body: { strokeWidth: 2.5, stroke: typeStrokeColors[node.getData()?.type || 'object'] },
    portTop: { circle: { opacity: 0 } },
    portBottom: { circle: { opacity: 0 } },
    portLeft: { circle: { opacity: 0 } },
    portRight: { circle: { opacity: 0 } },
  });
}

export function ensureConnectionPoint(cfg: any): any {
  if (!cfg) return cfg;
  if (cfg.port === 'port-arrow') {
    cfg.connectionPoint = { name: 'anchor' };
  }
  return cfg;
}

export function anchorForPort(port: string): EdgeAnchorName {
  if (port === 'port-top') return 'top';
  if (port === 'port-bottom') return 'bottom';
  if (port === 'port-left') return 'left';
  return 'right';
}

export function pickStartDirection(sourceNode: any, targetNode: any): EdgeAnchorName {
  const sBox = sourceNode.getBBox();
  const tBox = targetNode.getBBox();
  const sCx = sBox.x + sBox.width / 2;
  const sCy = sBox.y + sBox.height / 2;
  const tCx = tBox.x + tBox.width / 2;
  const tCy = tBox.y + tBox.height / 2;
  const dx = tCx - sCx;
  const dy = tCy - sCy;

  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0 ? 'right' : 'left';
  }
  return dy >= 0 ? 'bottom' : 'top';
}

export function pickTargetPortConfig(_sourceNode: any, targetNode: any): { cell: string } {
  return { cell: targetNode.id };
}

export function updateOptionArrowVisibility(node: any, graph: any): void {
  if (!node || !node.hasPort?.('port-arrow')) return;
  const hasArrowEdge = graph.getConnectedEdges(node).some((edge: any) => {
    const src = edge.getSource?.();
    const tgt = edge.getTarget?.();
    return (src?.port === 'port-arrow') || (tgt?.port === 'port-arrow');
  });
  const opacity = hasArrowEdge ? 0 : 1;
  node.setPortProp('port-arrow', 'attrs/arrowLine/opacity', opacity);
  node.setPortProp('port-arrow', 'attrs/arrowHead/opacity', opacity);
}

export function toggleEdgeCssClass(
  edge: any,
  graph: any,
  className: string,
  active: boolean,
): void {
  if (!edge) return;
  const view = graph.findViewByCell(edge);
  if (!view?.container) return;
  if (active) {
    view.container.classList.add(className);
  } else {
    view.container.classList.remove(className);
  }
}

export function sendEdgeToBack(edge: any, graph: any): void {
  if (!edge) return;
  if (isInvocationGuideEdge(edge)) return;
  const z = (edge as any).getZIndex?.();
  if (z === 9999) return;

  const sourceId = edge.getSourceCellId?.();
  const targetId = edge.getTargetCellId?.();
  const sourceNode = sourceId ? (graph.getCellById(sourceId) as any) : null;
  const targetNode = targetId ? (graph.getCellById(targetId) as any) : null;

  if (sourceNode?.isNode?.() && targetNode?.isNode?.()) {
    const sourceZ = sourceNode.getZIndex?.();
    const targetZ = targetNode.getZIndex?.();
    if ((sourceNode.getParent?.() || targetNode.getParent?.()) && (edge as any).setZIndex) {
      const baseZ = Math.max(
        typeof sourceZ === 'number' ? sourceZ : 0,
        typeof targetZ === 'number' ? targetZ : 0,
      );
      (edge as any).setZIndex(baseZ + 10);
      return;
    }
    const srcData = sourceNode.getData?.() || {};
    const tgtData = targetNode.getData?.() || {};
    const srcParent = sourceNode.getParent?.();
    const tgtParent = targetNode.getParent?.();
    const isSiblingEdge = !!srcParent && !!tgtParent && srcParent.id === tgtParent.id;
    const isChildToParent = !!srcParent?.isNode?.() && !!tgtData.isParentCopy && srcParent.id === targetNode.id;
    const isParentToChild = !!tgtParent?.isNode?.() && !!srcData.isParentCopy && tgtParent.id === sourceNode.id;
    if (isSiblingEdge || isChildToParent || isParentToChild) {
      const parentNode = isSiblingEdge ? srcParent : isChildToParent ? srcParent : tgtParent;
      const parentZ = parentNode?.getZIndex?.();
      const maxNodeZ = Math.max(
        typeof sourceZ === 'number' ? sourceZ : 0,
        typeof targetZ === 'number' ? targetZ : 0,
        typeof parentZ === 'number' ? parentZ : 0,
      );
      if ((edge as any).setZIndex) {
        (edge as any).setZIndex(maxNodeZ + 10);
      } else {
        edge?.toFront?.();
      }
      return;
    }
  }

  edge?.toBack?.();
}
